// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Body, Button, Card, LgPadding } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import {
  type FertilityCycleType,
  FertilityCycleTypeMap,
} from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle';
import React, { useEffect, useState } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { OrderEarlyFormFields } from './OrderEarlyFormFields';

export const OrderEarlyForm = () => {
  const { getParam, goBack, navigate } = useNavigation<'RouteOrderEarlyForm'>();
  const prescriptionId = getParam('prescriptionId');
  const { paymentMethods } = useGetPaymentMethods();
  const { trackEvent } = useAnalytics();

  // form inputs
  const [employer, setEmployer] = useState<string>('');
  const [cycleType, setCycleType] = useState<FertilityCycleType | undefined>(undefined);
  const [selectedBackupPaymentMethodId, setSelectedBackupPaymentMethodId] = useState<number | undefined>(undefined);

  // autofill the most recently created payment method
  useEffect(() => {
    if (paymentMethods.length === 0) return;
    setSelectedBackupPaymentMethodId(Math.max(...paymentMethods.map((pm) => pm.id)));
  }, [paymentMethods]);

  // input handlers
  const handleChangeEmployer = (value?: string) => {
    setEmployer(value || '');
  };

  const handleSelectCycleType = (value: string) => {
    const validCycleTypes = Object.values(FertilityCycleTypeMap) as readonly string[];
    const selectedCycleType = validCycleTypes.includes(value) ? (value as FertilityCycleType) : undefined;
    setCycleType(selectedCycleType);
  };

  const handleSelectPaymentMethod = (value: string) => {
    if (value === 'create_new') {
      // when the user selects "New Payment Method" from the dropdown,
      // we should navigate them to the payment method creation page
      // (which is named "SELECT_PAYMENT_METHOD" for some reason)
      trackEvent({ event: EVENTS.ADD_NEW_METHOD_TAPPED });
      setSelectedBackupPaymentMethodId(undefined);
      navigate('RouteSelectPaymentMethod');
    } else {
      // otherwise, they have selected a payment method
      const id = parseInt(value, 10);
      setSelectedBackupPaymentMethodId(isNaN(id) ? undefined : id);
    }
  };

  // submission handler: checks for input errors and navigates to the next page
  const handlePressedNext = () => {
    navigate('RouteOrderEarlySubmit', {
      employer,
      cycleType,
      selectedBackupPaymentMethodId,
      prescriptionId,
    });
  };

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      NavBarCenterContent="Order Early"
      onDismiss={() => {
        goBack();
      }}
      HeaderContent={
        <LgPadding topPadding={SPACING.STATIC.SM}>
          <Body color={COLORS.TEXT_COLORS.GREY}>
            Request early ordering of your Progyny formulary medications. We will reach out for you to schedule in
            advance of your start date.
            {'\n\n'}
            Nothing will be charged at this time. After you order your medication, if your coverage is denied, then you
            will be responsible for the full cost of the drug.
          </Body>
        </LgPadding>
      }
      buttons={[
        <Button
          key="next"
          label="Next"
          onPress={handlePressedNext}
          disabled={!employer || !cycleType || !selectedBackupPaymentMethodId}
        />,
      ]}
    >
      <Card>
        <LgPadding>
          <OrderEarlyFormFields
            paymentMethods={paymentMethods}
            selectedBackupPaymentMethodId={selectedBackupPaymentMethodId}
            handleChangeEmployer={handleChangeEmployer}
            handleSelectCycleType={handleSelectCycleType}
            handleSelectPaymentMethod={handleSelectPaymentMethod}
          />
        </LgPadding>
      </Card>
    </ProgynyPage>
  );
};
